import { useEffect, useState } from "react";
import { productApi, type ProductApi, type ProductSubscriptionSnapshot } from "../product-api.js";
import "./business-trial-status.css";

// WHY [BOL-SELF-SERVE-WEB-004]: the human chose a three-day trial that starts
// at the first WhatsApp connection the server confirms (R707/R739), never
// cuts off a paid business, and never applies to the Agency, Rentals or the
// direct-beta business (R769). The browser only presents the server's projection: it
// never starts, extends or ends a trial, and it shows nothing when the fields
// are absent (older API) or the business is paid/untimed. Chat already shows
// the server's own 403 text when a new Luna request is refused, so this line
// does not repeat it. Falsifier: a paid or beta business sees a countdown, a
// waiting business sees an end time, or a missing field renders a trial.

export type TrialPhase = "PAID" | "UNTIMED" | "WAITING_FOR_WHATSAPP" | "OPEN" | "ENDED";
type Subscription = ProductSubscriptionSnapshot["subscription"];

// UNTIMED means "show no trial clock": a legacy or beta trial, an older API
// without the fields, or a PAST_DUE/CANCELLED business (its own Subscription
// copy explains that state; its old trial dates must never read as a trial).
export function trialPhase(subscription: Subscription, nowMs: number): TrialPhase {
  if (subscription.state === "ACTIVE") return "PAID";
  if (subscription.state !== "TRIAL") return "UNTIMED";
  if (subscription.trialClockEligible !== true) return "UNTIMED";
  if (!subscription.trialStartedAt) return "WAITING_FOR_WHATSAPP";
  const endsAt = Date.parse(subscription.trialEndsAt ?? "");
  // A started clock without a readable end is a server contract fault; show
  // no countdown rather than invent one.
  if (!Number.isFinite(endsAt)) return "UNTIMED";
  return nowMs < endsAt ? "OPEN" : "ENDED";
}

export function trialEndLabel(endsAt: string, timeZone: string): string {
  const instant = new Date(endsAt);
  const zone = validTimeZone(timeZone) ? timeZone : "UTC";
  const day = new Intl.DateTimeFormat("en-GB", { timeZone: zone, weekday: "long", day: "numeric", month: "long" }).format(instant);
  const time = new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(instant);
  return `${day}, ${time} (${zone})`;
}

export function trialStatusText(phase: TrialPhase, subscription: Subscription, timeZone: string): string | null {
  if (phase === "WAITING_FOR_WHATSAPP") return "Your 3-day trial starts when you connect WhatsApp.";
  if (phase === "OPEN" && subscription.trialEndsAt) return `Trial ends ${trialEndLabel(subscription.trialEndsAt, timeZone)}`;
  if (phase === "ENDED") return "Your trial has ended. Please pay to continue.";
  return null;
}

export function TrialEndedNotice({ variant, mobileOnly = false }: { variant: "topbar" | "panel"; mobileOnly?: boolean }) {
  return <p className={`trial-status trial-status--${variant} trial-status--ended trial-status--with-action${mobileOnly ? " trial-status--mobile-expired" : ""}`} role="status">
    <span>Your trial has ended. Please pay to continue.</span>
    <button type="button" className="trial-status__action" onClick={() => { window.location.hash = "#settings/subscription"; }}>Go to payments</button>
  </p>;
}

export function BusinessTrialStatus({
  variant, businessTimezone, openWhatsApp, refreshKey, subscription, apiClient = productApi, onEndedChange
}: {
  variant: "topbar" | "panel";
  businessTimezone: string;
  openWhatsApp?: (() => void) | undefined;
  refreshKey?: string | undefined;
  subscription?: Subscription | undefined;
  apiClient?: Pick<ProductApi, "subscription">;
  onEndedChange?: ((ended: boolean) => void) | undefined;
}) {
  const [fetched, setFetched] = useState<Subscription | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    // The panel passes its already-loaded snapshot; only the desk line reads.
    if (subscription) return;
    let active = true;
    apiClient.subscription()
      .then((result) => { if (active) setFetched(result.subscription.subscription); })
      // Informational only: no product session (legacy owner login) or any
      // failure renders nothing instead of a guessed trial state.
      .catch(() => { if (active) setFetched(null); });
    return () => { active = false; };
  }, [apiClient, refreshKey, subscription]);
  const current = subscription ?? fetched;
  const phase = current ? trialPhase(current, nowMs) : null;
  // One existing canonical read/clock owns both desktop and mobile notices.
  // Unknown, pre-WhatsApp, active and paid phases never create a mobile banner.
  useEffect(() => { onEndedChange?.(phase === "ENDED"); }, [phase, onEndedChange]);
  const endsAtMs = current?.trialEndsAt ? Date.parse(current.trialEndsAt) : Number.NaN;
  useEffect(() => {
    // Flip OPEN to ENDED at the server's end time without another request.
    if (phase !== "OPEN" || !Number.isFinite(endsAtMs)) return;
    const wait = Math.min(Math.max(endsAtMs - Date.now(), 0) + 1_000, 2_147_000_000);
    const timer = window.setTimeout(() => setNowMs(Date.now()), wait);
    return () => window.clearTimeout(timer);
  }, [phase, endsAtMs]);
  if (!current || !phase) return null;
  const text = trialStatusText(phase, current, businessTimezone);
  if (!text) return null;
  const className = `trial-status trial-status--${variant} trial-status--${phase.toLowerCase().replaceAll("_", "-")}`;
  // WHY [BOL-TRIAL-PAYMENT-NAV-875]: an ended notice had no way to reach
  // payment. This only navigates to the existing authenticated section; it
  // never requests an invoice, reports a transfer, confirms payment or unlocks
  // access. Preserve canonical trial eligibility/dates and ordinary history.
  if (phase === "ENDED") {
    return <TrialEndedNotice variant={variant} />;
  }
  if (phase === "WAITING_FOR_WHATSAPP" && openWhatsApp) {
    return <p className={`${className} trial-status--with-action`} role="status">
      <span>{text}</span>
      <button type="button" className="trial-status__action" onClick={openWhatsApp}>Connect WhatsApp</button>
    </p>;
  }
  return <p className={className} role="status" title={text}><span>{text}</span></p>;
}

function validTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone });
    return true;
  } catch {
    return false;
  }
}
